/**
 * Usage, cost per use, and intention history (Epics 5 and 6).
 *
 * These are the questions the product exists to answer -- what is going unused,
 * what a thing has actually cost per wearing, what was decided about it and when
 * -- and all of them are folds over records the merge already maintains. None
 * needs the server.
 */
import { decode } from './hlc.js';
import { filterItems } from './search.js';
import { fieldValue, isArchived, isDeleted, isProtected } from './merge.js';
/** Whole days between two calendar dates, positive when `later` is after `earlier`. */
export function daysBetween(earlier, later) {
    const ms = Date.parse(`${later}T00:00:00Z`) - Date.parse(`${earlier}T00:00:00Z`);
    return Math.round(ms / 86_400_000);
}
/** Today as a calendar date in UTC, which is what every date in the model is. */
export const today = (at = new Date()) => at.toISOString().slice(0, 10);
// ---------------------------------------------------------------------------
// Intention history (FR-5.4) and the review queue (FR-5.8)
// ---------------------------------------------------------------------------
/** Every verdict ever recorded, newest first. History is never rewritten. */
export function intentionHistory(item) {
    return [...item.intentions.values()].sort((a, b) => (a.hlc < b.hlc ? 1 : -1));
}
/** Whether an item's review-again date has passed (FR-5.5). */
export function isReviewDue(item, asOf = today()) {
    return item.reviewAgainOn !== null && item.reviewAgainOn <= asOf;
}
export function tallyIntentions(manifest) {
    const counts = { keep: 0, uncertain: 0, release: 0, invest: 0, unreviewed: 0 };
    let total = 0;
    for (const item of manifest.items.values()) {
        if (isDeleted(item) || isArchived(item))
            continue;
        total += 1;
        counts[item.currentIntention ?? 'unreviewed'] += 1;
    }
    return { counts, total };
}
/**
 * The date an uncertain item is waiting on.
 *
 * The reconsider date when there is one, and otherwise the day the verdict was
 * recorded. Uncertain is prompted for a date (FR-5.5) but not required to have
 * one, and an undated "ask me again later" is still waiting on something -- the
 * day it was deferred. Treating it as undated would push it behind every dated
 * item, which is the opposite of what an older deferral deserves.
 */
function waitingSince(item) {
    if (item.reviewAgainOn !== null)
        return item.reviewAgainOn;
    const at = lastReviewedAt(item);
    return at === null ? '' : new Date(at).toISOString().slice(0, 10);
}
const byName = (a, b) => String(fieldValue(a, 'name') ?? '').localeCompare(String(fieldValue(b, 'name') ?? ''));
/**
 * Items a review session should offer, in the order FR-5.8 asks for.
 *
 * Two groups and nothing else: things never given a verdict, then things whose
 * verdict was "ask me again later". A review is for deciding what has not been
 * decided, and an item marked Keep, Release or Invest *has* been decided -- it
 * was offered anyway, on the reasoning that a session should be able to continue
 * past the backlog, which in practice meant the queue never emptied and the end
 * of the real work was invisible.
 *
 * Unreviewed leads because it has waited longest in the only sense that counts:
 * it has never been considered at all. Within it, by name, which is arbitrary
 * and stable -- there is no date to order by, because nothing has happened to
 * these items yet.
 *
 * Uncertain follows, oldest date first, so the longest-deferred decision is the
 * one put in front of someone. Note that a date in the future does not exclude
 * an item: the user asked to be reminded then, not to be forbidden from looking
 * now, and a session they opened deliberately is them looking.
 */
export function reviewQueue(manifest) {
    const active = [...manifest.items.values()].filter((item) => !isDeleted(item) && !isArchived(item));
    const unreviewed = active.filter((item) => item.currentIntention === null).sort(byName);
    const uncertain = active
        .filter((item) => item.currentIntention === 'uncertain')
        .sort((a, b) => waitingSince(a).localeCompare(waitingSince(b)) || byName(a, b));
    return [...unreviewed, ...uncertain];
}
// ---------------------------------------------------------------------------
// Review sessions (FR-5.6 to FR-5.10)
// ---------------------------------------------------------------------------
/**
 * When an item was last given a verdict, in epoch milliseconds.
 *
 * Read from the clock on the newest intention record rather than from a separate
 * timestamp field. A hybrid logical clock carries the writing device's wall time
 * in its high bits, which is the same thing a timestamp here would record, and
 * storing it twice would create two values that could disagree.
 *
 * It is a high-water mark, so a device whose clock ran fast reports a review as
 * slightly more recent than it was. At the day granularity this is used for,
 * that does not change an answer.
 */
export function lastReviewedAt(item) {
    const newest = intentionHistory(item)[0];
    if (newest === undefined)
        return null;
    try {
        return decode(newest.hlc).millis;
    }
    catch {
        return null;
    }
}
/** Null for an item that has never been reviewed, which is not the same as "a long time ago". */
export function daysSinceReview(item, asOf = today()) {
    const at = lastReviewedAt(item);
    if (at === null)
        return null;
    return daysBetween(new Date(at).toISOString().slice(0, 10), asOf);
}
/**
 * The items a session still has to offer.
 *
 * `handled` carries the ids already decided or skipped in *this* session. Skipped
 * items are excluded from the rest of the session but not from the next one,
 * which is what FR-5.9 asks: skipping is "not now", not "never again".
 *
 * Recomputed from the manifest on every call rather than frozen at the start, so
 * a session survives the catalog changing underneath it -- an item deleted on
 * another device mid-session simply stops being offered.
 */
export function sessionQueue(manifest, scope, handled, index, asOf = today()) {
    const { notReviewedForDays, ...filter } = scope;
    const inScope = filterItems(manifest, { ...filter, visibility: 'active' }, index).filter((item) => {
        if (notReviewedForDays === undefined)
            return true;
        const since = daysSinceReview(item, asOf);
        // Never reviewed counts as overdue by any measure: it is the strongest
        // case for being offered, not a missing value to skip over.
        return since === null || since >= notReviewedForDays;
    });
    const eligible = new Set(inScope.map((item) => item.itemId));
    return reviewQueue(manifest).filter((item) => eligible.has(item.itemId) && !handled.has(item.itemId));
}
/**
 * Items whose review date has arrived (FR-5.12).
 *
 * Distinct from `reviewQueue`, which orders the *whole* active catalog for a
 * session and puts the unreviewed first. Both are useful and they are not
 * interchangeable: a reminder built on the queue announced "3 items are due for
 * review" because three items existed, which is a notification that would never
 * stop and would teach the user to ignore every later one.
 *
 * Never-reviewed items are deliberately excluded. An item does not *become* due
 * by sitting there; it becomes due on a date the user chose when they said they
 * were uncertain. Counting everything unreviewed would make the reminder
 * permanent from the moment anything was added, which is the nagging
 * `NFR-SAFE-2` exists to prevent.
 */
export function dueForReview(manifest, asOf = today()) {
    const due = [];
    for (const item of manifest.items.values()) {
        if (isDeleted(item) || isArchived(item))
            continue;
        if (isReviewDue(item, asOf))
            due.push(item);
    }
    return due;
}
/** A default to offer when someone says they are uncertain (FR-5.5). */
export const DEFAULT_REVIEW_AGAIN_DAYS = 90;
export function defaultReviewAgainOn(asOf = today(), days = DEFAULT_REVIEW_AGAIN_DAYS) {
    const at = new Date(`${asOf}T00:00:00Z`);
    at.setUTCDate(at.getUTCDate() + days);
    return at.toISOString().slice(0, 10);
}
/**
 * Splits a selection into what a bulk action may touch and what it may not.
 *
 * `NFR-SAFE-3` excludes protected items from bulk intention changes and requires
 * individual confirmation to delete or release them. This is the first half, and
 * it is a split rather than a filter on purpose: a bulk action that quietly
 * dropped the protected items would leave the user believing it had applied to
 * everything they selected. The caller gets both lists and has to say what it
 * did not do.
 *
 * Deliberately here, in the domain, rather than in whichever screen grows a
 * multi-select first. The exclusion is a property of the requirement, not of a
 * particular button, and a second screen must not have to rediscover it.
 */
export function forBulkAction(items) {
    const allowed = [];
    const withheld = [];
    for (const item of items) {
        if (isProtected(item))
            withheld.push(item);
        else
            allowed.push(item);
    }
    return { allowed, withheld };
}
//# sourceMappingURL=review.js.map